import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ArrowRight, FolderKanban } from "lucide-react";
import Pagination from "src/components/Pagination";
import Status from "src/components/Status/Status";
import Table from "src/components/Table/Table";
import EntityAvatar from "src/components/EntityAvatar";
import PageShell from "src/components/PageShell";
import BackLink from "src/components/BackLink";
import Tabs from "src/components/Tabs";
import { SectionHeader, EmptyState } from "src/components/ui-x";
import useAlert from "src/hooks/use-alert";
import { useWorkHistoryQuery } from "src/lib/endpoints/profile/profile.api";

const ActiveProjects = () => {
  const { pathname } = useLocation();
  const siblingCount = 1;
  const pageColumn = 1;
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const { workHistory } = useWorkHistoryQuery(
    page + 1,
    searchParams.get("status") ?? "on-going"
  );
  const [totalCount, setTotalCount] = useState(0);
  const { state, open, close, Alert } = useAlert();

  const standalone = pathname.includes("projects");
  const status = searchParams.get("status") ?? "on-going";

  const handlePageChange = useCallback(
    (_page: number) => {
      setPage(_page - 1);
    },
    [page]
  );

  const tabHeading = useMemo(
    () => [
      {
        id: 1,
        title: "on-going",
      },
      {
        id: 2,
        title: "finished",
      },
    ],
    []
  );

  const tableHeader = useMemo(
    () => [
      {
        title: "Client",
        key: "client",
      },
      {
        title: "Project",
        key: "project",
      },
      {
        title: "Date",
        key: "date",
      },
      {
        title: "Status",
        key: "status",
      },
      {
        title: "",
        key: "details",
      },
    ],
    []
  );

  const modifiedData = useMemo(() => {
    return workHistory?.data?.map((each) => [
      <div className="flex items-center gap-3">
        <EntityAvatar
          src={each?.projectDetailJobDetail?.companyLogo}
          name={each?.projectDetailJobDetail?.companyName}
        />
        <span className="font-semibold capitalize text-brand-navy">
          {each?.projectDetailJobDetail?.companyName ?? ""}
        </span>
      </div>,
      <span className="capitalize text-muted-foreground">
        {each?.projectDetailJobDetail?.jobTitle ?? ""}
      </span>,
      <span className="tabular-nums text-muted-foreground">
        {format(each?.startDate ?? new Date().getTime(), "EEE, MMM d")}
      </span>,
      <Status label={each?.status} />,
      <Link
        className="inline-flex h-8 items-center rounded-md border border-brand-navy px-3 text-xs font-semibold text-brand-navy transition-colors hover:bg-surface"
        to={`/project/${each.job_detail_id}`}
      >
        Details
      </Link>,
    ]);
  }, [workHistory]);

  useEffect(() => {
    setTotalCount(workHistory?.total ?? 0);
  }, [workHistory]);

  useEffect(() => {
    if (!searchParams.get("status")) {
      setSearchParams({ status: "on-going" });
    }
  }, []);

  if (!modifiedData) return null;

  const tabs = (
    <Tabs
      items={tabHeading}
      value={status}
      onChange={(title) => setSearchParams({ status: title })}
    />
  );

  const table = (
    <Table
      header={tableHeader}
      eyebrow={standalone ? undefined : "Work history"}
      label={standalone ? undefined : "Projects"}
      actions={
        standalone ? undefined : (
          <>
            {tabs}
            <Link
              to="/projects"
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"
            >
              See all
              <ArrowRight className="h-3 w-3" />
            </Link>
          </>
        )
      }
      column={modifiedData}
      defaultDisplay={
        <EmptyState
          className="border-0 py-10"
          icon={<FolderKanban className="h-6 w-6" />}
          title={`No ${status} projects`}
          description="Projects you take on will be listed here."
        />
      }
    />
  );

  if (!standalone) return table;

  return (
    <PageShell wide>
      <BackLink to="/project" label="Projects" />
      <SectionHeader
        eyebrow="Work history"
        title="Projects"
        description="Everything you are working on, and everything you have delivered."
        actions={tabs}
      />

      {table}

      <Pagination
        rangeStart={
          totalCount !== 0 ? (page || 0) * ((pageSize || 0) * pageColumn) + 1 : 0
        }
        totalCount={totalCount}
        currentPage={(page || 0) + 1}
        pageSize={pageSize}
        pageColumn={pageColumn}
        siblingCount={siblingCount}
        onPageChange={handlePageChange}
      />

      <span className="fixed right-3 top-3 z-30">
        <Alert />
      </span>
    </PageShell>
  );
};

export default ActiveProjects;